import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { rememberPostLoginRedirect } from "./utils/postLoginRedirect";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./constant/LanguageContext";
import { ThemeProvider } from "./constant/theme";
import { NavigationProvider } from './constant/NavigationContext';
import { ModalProvider } from './constant/ModalContext';
import { sessionManager } from './utils/sessionManager';
import { MessengerProvider } from './context/MessengerContext';
import { NotificationProvider } from './context/NotificationContext';
import { MiniChatProvider } from './pages/(All) messenger/context/MiniChatContext';
import MiniChatDock from './pages/(All) messenger/components/MiniChatDock';
import MiniChatErrorBoundary from './pages/(All) messenger/components/MiniChatErrorBoundary';
import ClientBadge from "./components/ClientBadge";
import BroadcastCenter from "./components/Broadcasts/BroadcastCenter";
import IdleSessionGuard from "./components/IdleSessionGuard";
import SessionGate from "./components/session/SessionGate";
import AppErrorBoundary from "./components/session/AppErrorBoundary";
import { getRoleHomePath } from "./utils/roleHome";

// Main Pages
import LandingPage from "./pages/Landing/LandingPage";
import Login from "./pages/Login/LoginPage";
import Dashboard from "./pages/Admin_Acount/Admin_Acount";
import UserProfile from "./pages/User_Account";
import UserProfileAdmin from "./pages/(Admin) Profile";
import PasswordResetPage from './pages/Login/PasswordResetPage';
import ProfessionalDashboardManager from './pages/Manager_Account/index';
import Mo3tiUserProfilemanager from './pages/(Manager)user-profile/Mo3tiUserProfile';
import DashboardSuper from "./pages/(SuperAdmin)Profile";

// Role definitions
const ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  USER: "user",
  SUPER: "superadmin"
};

// ✅ ENHANCED PrivateRoute with session management
function PrivateRoute({ children, allowedRoles = [] }) {
  const { user } = useAuth();
  const location = useLocation();

  // ✅ Check both session manager AND AuthContext
  const isAuthenticated = sessionManager.isAuthenticated();
  const userRole = sessionManager.getUserRole()?.toLowerCase();

  // ✅ If not authenticated in session manager, try to restore from storage
  if (!isAuthenticated) {
    // Try to get user from storage
    const storedUser = sessionManager.getUser();
    if (!storedUser) {
      // Remember a personal-page deep link (e.g. the "salary paid" email's
      // /userprofile/salary?year=&month=) so login can return there.
      rememberPostLoginRedirect(location.pathname + location.search);
      return <Navigate to="/login" replace />;
    }
  }

  // ✅ Use userRole from session manager or user object as fallback
  const effectiveRole = userRole || user?.role?.toLowerCase();

  // From here on there is a session worth trying; what the user actually
  // sees (verifying / connection problem / the app) depends on the server
  // confirming the identity — see components/session/SessionGate.jsx.
  if (allowedRoles.length === 0) {
    return <SessionGate>{children}</SessionGate>;
  }

  const hasAccess = allowedRoles.some(role => role.toLowerCase() === effectiveRole);

  if (!hasAccess) {
    // ✅ Redirect to appropriate dashboard based on role
    if (effectiveRole === "user") {
      return <Navigate to="/userprofile" replace />;
    } else if (effectiveRole === "manager") {
      return <Navigate to="/DashboardManager" replace />;
    } else if (effectiveRole === "admin") {
      return <Navigate to="/dashboard" replace />;
    } else if (effectiveRole === "superadmin") {
      return <Navigate to="/DashboardSuper" replace />;
    }
    // If no role matches, redirect to login
    return <Navigate to="/login" replace />;
  }

  return <SessionGate>{children}</SessionGate>;
}

// ✅ ENHANCED Default Route Component
function DefaultRoute() {
  const { user } = useAuth();

  // ✅ Check both session manager AND user object
  const isAuthenticated = sessionManager.isAuthenticated();
  const userRole = (sessionManager.getUserRole() || user?.role)?.toLowerCase();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on role (fallback to login if the role is not recognized)
  return <Navigate to={getRoleHomePath(userRole) || "/login"} replace />;
}

// Rendering crashes inside the app get the Nexploy error screen instead of a
// blank page; navigating to another route clears it.
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <AppErrorBoundary resetKey={location.pathname}>{children}</AppErrorBoundary>;
}

export default function App() {
  return (
    // Last line of defence: catches a crash in the providers themselves.
    <AppErrorBoundary>
      <AppTree />
    </AppErrorBoundary>
  );
}

function AppTree() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <LanguageProvider>
          <MessengerProvider>
            {/* Router wraps NotificationProvider (not the reverse) so
                NotificationContext can call useLocation() to derive the
                current View for view-scoped notification fetching — see
                NotificationContext.js. Everything that was already inside
                NotificationProvider stays exactly where it was. */}
            <Router>
              <NotificationProvider>
                <ClientBadge />
                {/* Superadmin announcements (banner / toast / pop-up) for every role */}
                <BroadcastCenter />
                <IdleSessionGuard to="/login" idleTime={900000} warningTime={60000} />
                <MiniChatErrorBoundary>
                  <MiniChatProvider>
                    <MiniChatDock />
                  </MiniChatProvider>
                </MiniChatErrorBoundary>
                <RouteErrorBoundary>
                  <ModalProvider>
                    <NavigationProvider>
                      <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/reset-password" element={<PasswordResetPage />} />

                        {/* Admin Dashboard - Only admins */}
                        <Route
                          path="/dashboard/*"
                          element={
                            <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                              <Dashboard />
                            </PrivateRoute>
                          }
                        />

                        {/* Manager Dashboard - Only managers */}
                        <Route
                          path="/DashboardManager/*"
                          element={
                            <PrivateRoute allowedRoles={[ROLES.MANAGER]}>
                              <ProfessionalDashboardManager />
                            </PrivateRoute>
                          }
                        />

                        {/* User Profile - Only regular users */}
                        <Route
                          path="/userprofile/*"
                          element={
                            <PrivateRoute allowedRoles={[ROLES.USER]}>
                              <UserProfile />
                            </PrivateRoute>
                          }
                        />

                        {/* Admin viewing employee profile */}
                        <Route
                          path="/userprofileadmin/*"
                          element={
                            <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                              <UserProfileAdmin />
                            </PrivateRoute>
                          }
                        />

                        {/* Manager viewing employee profile */}
                        <Route
                          path="/userprofilemanager/*"
                          element={
                            <PrivateRoute allowedRoles={[ROLES.MANAGER]}>
                              <Mo3tiUserProfilemanager />
                            </PrivateRoute>
                          }
                        />

                        <Route
                          path="/DashboardSuper/*"
                          element={
                            <PrivateRoute allowedRoles={[ROLES.SUPER]}>
                              <DashboardSuper />
                            </PrivateRoute>
                          }
                        />

                        {/* Catch-all route - Redirects to default route */}
                        <Route path="*" element={<DefaultRoute />} />
                      </Routes>
                    </NavigationProvider>
                  </ModalProvider>
                </RouteErrorBoundary>
              </NotificationProvider>
            </Router>
          </MessengerProvider>
        </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}